import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Snackbar,
  Tooltip,
} from '@mui/material';
import { Add, Refresh, FilterList, EventAvailable, Today, Person } from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  TypePill,
  TableEmptyState,
} from '../../components/common/pageKit';
import crmService, {
  CrmActivity,
  ActivityType,
  ACTIVITY_TYPES,
  ActivityEntityType,
  ACTIVITY_ENTITY_TYPES,
  CrmSummary,
} from '../../services/crmService';
import { EntityPicker } from '../../components/common/EntityPicker';

const activityTints: Record<ActivityType, string> = {
  CALL: '#0284c7',
  EMAIL: '#7c3aed',
  MEETING: '#ea580c',
  NOTE: '#64748b',
  TASK: '#d97706',
  DEMO: '#059669',
};

interface ActivityFormData {
  entityType: ActivityEntityType;
  entityId: string;
  activityType: ActivityType;
  subject: string;
  notes: string;
  outcome: string;
  occurredAt: string;
  durationMins: string;
  followUpAt: string;
}

const nowForInput = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const emptyForm: ActivityFormData = {
  entityType: 'lead',
  entityId: '',
  activityType: 'CALL',
  subject: '',
  notes: '',
  outcome: '',
  occurredAt: nowForInput(),
  durationMins: '',
  followUpAt: '',
};

const formatDateTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const ActivitiesPage: React.FC = () => {
  const [activities, setActivities] = useState<CrmActivity[]>([]);
  const [upcoming, setUpcoming] = useState<CrmActivity[]>([]);
  const [summary, setSummary] = useState<CrmSummary | null>(null);

  const [entityTypeFilter, setEntityTypeFilter] = useState<ActivityEntityType | ''>('');
  const [activityTypeFilter, setActivityTypeFilter] = useState<ActivityType | ''>('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<ActivityFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, upcomingRes] = await Promise.all([
        crmService.getActivities({
          entityType: entityTypeFilter || undefined,
          activityType: activityTypeFilter || undefined,
          from: fromFilter || undefined,
          to: toFilter || undefined,
        }),
        crmService.getUpcomingActivities(),
      ]);
      setActivities(listRes.activities);
      setUpcoming(upcomingRes.activities);
    } catch (err: any) {
      setError(err.message || 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  }, [entityTypeFilter, activityTypeFilter, fromFilter, toFilter]);

  const fetchSummary = useCallback(async () => {
    try {
      setSummary(await crmService.getSummary());
    } catch {
      // Stat row simply stays hidden.
    }
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const clearFilters = () => {
    setEntityTypeFilter('');
    setActivityTypeFilter('');
    setFromFilter('');
    setToFilter('');
  };

  const openLogDialog = (entityType?: ActivityEntityType, entityId?: number) => {
    setForm({
      ...emptyForm,
      occurredAt: nowForInput(),
      entityType: entityType || 'lead',
      entityId: entityId ? String(entityId) : '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const entityId = parseInt(form.entityId, 10);
    if (!entityId) {
      setToast({ message: 'entityId must be a positive number', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      await crmService.createActivity({
        entityType: form.entityType,
        entityId,
        activityType: form.activityType,
        subject: form.subject,
        notes: form.notes || undefined,
        outcome: form.outcome || undefined,
        occurredAt: form.occurredAt ? new Date(form.occurredAt).toISOString() : undefined,
        durationMins: form.durationMins ? parseInt(form.durationMins, 10) : undefined,
        followUpAt: form.followUpAt ? new Date(form.followUpAt).toISOString() : undefined,
      });
      setToast({ message: 'Activity logged', severity: 'success' });
      setDialogOpen(false);
      fetchActivities();
      fetchSummary();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to log activity', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const renderFollowUpRows = (rows: CrmActivity[], rowKeyPrefix: string) =>
    rows.map((a) => (
      <TableRow key={`${rowKeyPrefix}-${a.id}`} sx={bodyRowSx}>
        <TableCell sx={{ py: 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {a.subject}
          </Typography>
        </TableCell>
        <TableCell>
          <TypePill label={a.activity_type} tint={activityTints[a.activity_type]} />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {a.entity_type} #{a.entity_id}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{formatDateTime(a.follow_up_at)}</Typography>
        </TableCell>
        <TableCell>{a.owner_name || '—'}</TableCell>
      </TableRow>
    ));

  return (
    <Box>
      <PageHeader
        title="Activities"
        subtitle="Logged touchpoints across leads, opportunities and customers"
        actions={
          <>
            <IconButton onClick={fetchActivities} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={() => openLogDialog()}>
              Log Activity
            </Button>
          </>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 2,
          mb: 2.5,
        }}
      >
        <StatCard
          title="Activities This Week"
          value={summary ? summary.activitiesThisWeek : null}
          icon={<Today />}
          color="#0284c7"
        />
        <StatCard
          title="Upcoming Follow-ups"
          value={loading ? null : upcoming.length}
          icon={<EventAvailable />}
          color="#059669"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Upcoming follow-ups — leads, per the brief. */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Upcoming Follow-ups
      </Typography>
      <Box sx={{ mb: 3 }}>
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Subject', 'Type', 'Entity', 'Follow-up At', 'Owner'].map((h) => (
                    <TableCell key={h} sx={headerCellSx}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, border: 0 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : upcoming.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ border: 0 }}>
                      <TableEmptyState
                        title="No follow-ups due"
                        hint="Log an activity with a follow-up date to see it here."
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  renderFollowUpRows(upcoming, 'upcoming')
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      </Box>

      {/* Full timeline */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        All Activity
      </Typography>

      <FilterBar>
        <TextField
          select
          size="small"
          label="Entity Type"
          value={entityTypeFilter}
          onChange={(e) => setEntityTypeFilter(e.target.value as ActivityEntityType | '')}
          sx={{ minWidth: 160 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Entities</MenuItem>
          {ACTIVITY_ENTITY_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Activity Type"
          value={activityTypeFilter}
          onChange={(e) => setActivityTypeFilter(e.target.value as ActivityType | '')}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All Types</MenuItem>
          {ACTIVITY_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label="From"
          type="date"
          value={fromFilter}
          onChange={(e) => setFromFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          size="small"
          label="To"
          type="date"
          value={toFilter}
          onChange={(e) => setToFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />

        <FilterSpacer />

        {(entityTypeFilter || activityTypeFilter || fromFilter || toFilter) && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      <TableSurface>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {['Occurred At', 'Type', 'Subject', 'Entity', 'Outcome', 'Duration', 'Owner'].map((h) => (
                  <TableCell key={h} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : activities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No activity matches these filters"
                      hint="Clear the filters, or log a new touchpoint."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                activities.map((a) => (
                  <TableRow key={a.id} sx={bodyRowSx}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2">{formatDateTime(a.occurred_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <TypePill label={a.activity_type} tint={activityTints[a.activity_type]} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {a.subject}
                      </Typography>
                      {a.notes && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {a.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`${a.entity_type} #${a.entity_id}`}>
                        <Typography variant="body2" color="text.secondary">
                          {a.entity_type} #{a.entity_id}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{a.outcome || '—'}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {a.duration_mins !== null ? `${a.duration_mins} min` : '—'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Person sx={{ fontSize: 14, color: 'text.disabled' }} />
                        {a.owner_name || '—'}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      {/* Log activity dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Activity</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                fullWidth
                label="Entity Type"
                value={form.entityType}
                onChange={(e) => setForm((f) => ({ ...f, entityType: e.target.value as ActivityEntityType }))}
              >
                {ACTIVITY_ENTITY_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
              {/* Was a free-text "Entity ID" number box asking the user to go
                  and find a database id and type it back in. Now a searchable
                  lookup filtered to the chosen entity type. */}
              <EntityPicker
                entityType={String(form.entityType).toLowerCase() as any}
                value={form.entityId}
                onChange={(id) => setForm((f) => ({ ...f, entityId: id ? String(id) : '' }))}
                required
              />
            </Box>
            <TextField
              select
              fullWidth
              label="Activity Type"
              value={form.activityType}
              onChange={(e) => setForm((f) => ({ ...f, activityType: e.target.value as ActivityType }))}
            >
              {ACTIVITY_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Subject"
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              required
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Outcome"
              value={form.outcome}
              onChange={(e) => setForm((f) => ({ ...f, outcome: e.target.value }))}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Occurred At"
                type="datetime-local"
                value={form.occurredAt}
                onChange={(e) => setForm((f) => ({ ...f, occurredAt: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Duration (mins)"
                type="number"
                value={form.durationMins}
                onChange={(e) => setForm((f) => ({ ...f, durationMins: e.target.value }))}
              />
            </Box>
            <TextField
              fullWidth
              label="Follow-up At"
              type="datetime-local"
              value={form.followUpAt}
              onChange={(e) => setForm((f) => ({ ...f, followUpAt: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText="Optional — leave blank if no follow-up is needed"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.subject.trim() || !form.entityId}
          >
            {saving ? 'Saving…' : 'Log Activity'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default ActivitiesPage;
